import { useState, useEffect, useCallback, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package, RefreshCw, FileText, Download, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Dialog "DDT da Magazzino":
 * mostra tutti gli scarichi da magazzino verso un cantiere che non sono
 * ancora stati trasformati in DDT. L'admin può inserire il prezzo di
 * vendita al cliente e generare il DDT.
 */
export function DDTDaMagazzinoDialog({ open, onOpenChange, onCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [form, setForm] = useState({ prezzo_vendita: '', numero_ddt: '', data: '', note: '', iva_pct: 22 });

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/magazzino/movimenti/ddt-pending`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch (err) { toast.error('Errore caricamento pendenti'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchPending(); }, [open, fetchPending]);

  const openCreate = (mov) => {
    setCreatingId(mov.id);
    setForm({
      prezzo_vendita: mov.prezzo_vendita_suggerito || mov.prezzo_costo || '',
      numero_ddt: '',
      data: new Date().toISOString().slice(0, 10),
      note: '',
      iva_pct: 22,
    });
  };

  const cancelCreate = () => setCreatingId(null);

  const submitCreate = async (mov) => {
    const pv = parseFloat(form.prezzo_vendita);
    if (!(pv >= 0)) { toast.error('Inserisci un prezzo di vendita valido'); return; }
    try {
      const r = await axios.post(`${API}/magazzino/movimenti/${mov.id}/genera-ddt`, {
        prezzo_vendita: pv,
        numero_ddt: form.numero_ddt || '',
        data: form.data || '',
        note: form.note || '',
        iva_pct: parseFloat(form.iva_pct) || 22,
      }, { withCredentials: true });
      toast.success(`DDT ${r.data.numero} generato`);
      setCreatingId(null);
      await fetchPending();
      if (onCreated) onCreated(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore generazione DDT'); }
  };

  const downloadPdf = (mov) => {
    window.open(`${API}/magazzino/movimenti/${mov.id}/ddt-vendita-pdf`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-y-auto" data-testid="ddt-da-magazzino-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-600" />
            DDT da Magazzino — scarichi in attesa
            <Button size="sm" variant="ghost" onClick={fetchPending} disabled={loading} className="ml-auto">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Quando un operaio scarica materiali dal magazzino verso un cantiere,
            la riga finisce qui. Inserisci il prezzo di vendita al cliente e
            genera il DDT ufficiale (numerato e stampabile).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Package className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            Nessuno scarico in attesa di DDT.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Materiale</TableHead>
                <TableHead className="text-xs uppercase">Cantiere / Cliente</TableHead>
                <TableHead className="text-xs uppercase text-right">Q.tà</TableHead>
                <TableHead className="text-xs uppercase text-right">Costo</TableHead>
                <TableHead className="text-xs uppercase">Operatore</TableHead>
                <TableHead className="text-xs uppercase text-right w-[130px]">Azione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(m => {
                const isEditing = creatingId === m.id;
                const dateStr = m.created_at ? new Date(m.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                const isDone = m.ddt_status === 'created';
                return (
                  <Fragment key={m.id}>
                    <TableRow className="border-slate-100" data-testid={`ddt-pending-row-${m.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{dateStr}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-800">{m.materiale_nome}</div>
                        {m.codice_interno && <div className="text-[10px] text-slate-400 font-mono">{m.codice_interno}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium text-slate-800">{m.cantiere_nome || '—'}</div>
                        {m.cantiere_cliente && <div className="text-[10px] text-slate-500">{m.cantiere_cliente}</div>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{m.quantita} <span className="text-xs text-slate-400">{m.unita}</span></TableCell>
                      <TableCell className="text-right text-xs text-slate-600">€ {(m.costo_totale || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs">
                        <div className="text-slate-700">{m.user_name || '—'}</div>
                        <div className="text-[10px] text-slate-400">{m.user_email}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        {isDone ? (
                          <div className="flex items-center gap-1 justify-end">
                            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">DDT {m.ddt_numero}</Badge>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-purple-700" title="Scarica PDF" onClick={() => downloadPdf(m)} data-testid={`ddt-pending-pdf-${m.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" onClick={() => openCreate(m)} disabled={isEditing} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid={`ddt-pending-create-${m.id}`}>
                            <FileText className="h-3.5 w-3.5 mr-1" />Crea DDT
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {isEditing && (
                      <TableRow key={`${m.id}-edit`} className="bg-amber-50/50 border-amber-200">
                        <TableCell colSpan={7} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                            <div className="space-y-1">
                              <Label className="text-xs">Prezzo vendita cliente (€/{m.unita}) *</Label>
                              <Input type="number" step="0.01" value={form.prezzo_vendita} onChange={(e) => setForm({ ...form, prezzo_vendita: e.target.value })} data-testid="ddt-pending-prezzo" autoFocus />
                              <p className="text-[10px] text-slate-500">Costo interno: € {(m.prezzo_costo || 0).toFixed(2)} — Suggerito: € {(m.prezzo_vendita_suggerito || 0).toFixed(2)}</p>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Numero DDT (auto se vuoto)</Label>
                              <Input value={form.numero_ddt} onChange={(e) => setForm({ ...form, numero_ddt: e.target.value })} placeholder="Es. 2026/145" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Data DDT</Label>
                              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">IVA %</Label>
                              <Input type="number" step="1" value={form.iva_pct} onChange={(e) => setForm({ ...form, iva_pct: e.target.value })} />
                            </div>
                            <div className="space-y-1 md:col-span-5">
                              <Label className="text-xs">Note (opzionale)</Label>
                              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Es. consegna in cantiere 15/09..." />
                            </div>
                            <div className="md:col-span-5 flex items-center justify-between">
                              <div className="text-sm text-slate-700">
                                Totale imponibile: <b>€ {((parseFloat(form.prezzo_vendita) || 0) * (m.quantita || 0)).toFixed(2)}</b>
                                <span className="text-xs text-slate-500 ml-2">
                                  (IVA {form.iva_pct}% = € {(((parseFloat(form.prezzo_vendita) || 0) * (m.quantita || 0)) * ((parseFloat(form.iva_pct) || 0) / 100)).toFixed(2)})
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={cancelCreate}>Annulla</Button>
                                <Button size="sm" onClick={() => submitCreate(m)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ddt-pending-submit">
                                  Genera DDT <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DDTDaMagazzinoDialog;
